import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { useShallow } from "zustand/react/shallow";
import { trpc } from "@/app/api/trpcClient";
import { LoadingCard } from "@/app/components/LoadingCard";
import { MemberModal } from "@/app/components/MemberModal";
import { TeamSection } from "@/app/components/TeamSection";
import { useAvatars } from "@/app/hooks/useAvatars";
import { type TeamMember, useDagStore } from "@/app/hooks/useDagStore";
import { useDataSync } from "@/app/hooks/useDataSync";
import { matchesRoleFilter } from "@/app/utils/roleFilters";
import { addDays, toCalendarDateInput } from "@/app/utils/workstreams/dateUtils";
import type { Workstream } from "@/src/core/workstreams";

// Default filter values
export const SEARCH_DEFAULTS = {
  scopeStatus: "Current" as const,
  roleFilter: "Core Team" as const,
  excludedTeams: [] as string[],
  excludedProjects: [] as string[],
  cardsCollapsed: false,
};

const zSearch = z.object({
  memberId: z.string().optional(),
  scopeStatus: z.enum(["Current", "Future"]).optional(),
  roleFilter: z.enum(["All", "Core Team", "Core Team - FT", "Contractors", "Other"]).optional(),
  excludedTeams: z.array(z.string()).optional(),
  excludedProjects: z.array(z.string()).optional(),
  cardsCollapsed: z.boolean().optional(),
});

export type SearchParams = z.infer<typeof zSearch>;

// Helper to remove default values from search params to keep URLs clean
export function omitDefaultSearchParams(params: Record<string, unknown>): Partial<SearchParams> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    const defaultValue = SEARCH_DEFAULTS[key as keyof typeof SEARCH_DEFAULTS];
    // Preserve params not in SEARCH_DEFAULTS, or non-default values
    if (defaultValue === undefined || JSON.stringify(value) !== JSON.stringify(defaultValue)) {
      result[key] = value;
    }
  }
  return result;
}

function Dashboard() {
  const { isLoading } = useDataSync();
  const navigate = useNavigate({ from: Route.fullPath });
  const searchParams = Route.useSearch();
  const {
    memberId,
    scopeStatus = SEARCH_DEFAULTS.scopeStatus,
    roleFilter = SEARCH_DEFAULTS.roleFilter,
    excludedTeams = SEARCH_DEFAULTS.excludedTeams,
    excludedProjects = SEARCH_DEFAULTS.excludedProjects,
  } = searchParams;

  // Get data from store
  const teamMembers = useDagStore(useShallow((state) => state.teamMembersFiltered));
  const allProjects = useDagStore(useShallow((state) => state.allProjects));
  const teamMemberIndex = useDagStore(useShallow((state) => state.teamMemberIndex));
  const inScopeMemberEntitiesMap = useDagStore(
    useShallow((state) => state.inScope.memberEntitiesMap),
  );
  const setFilters = useDagStore(useShallow((state) => state.setFilters));
  const allTeams = useDagStore(useShallow((state) => state.allTeams));

  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const workstreamPreviewWindow = useMemo(() => {
    const today = new Date();
    return {
      startDate: toCalendarDateInput(addDays(today, -14)),
      endDate: toCalendarDateInput(addDays(today, 14)),
    };
  }, []);

  const workstreamsQuery = trpc.workstreams.list.useQuery(
    {
      startDate: workstreamPreviewWindow.startDate,
      endDate: workstreamPreviewWindow.endDate,
    },
    { staleTime: 30 * 1000 },
  );

  const workstreamsByMember = useMemo(() => {
    const grouped = new Map<string, Workstream[]>();
    for (const workstream of workstreamsQuery.data ?? []) {
      const current = grouped.get(workstream.personId) ?? [];
      current.push(workstream);
      grouped.set(workstream.personId, current);
    }
    return grouped;
  }, [workstreamsQuery.data]);

  // Sync URL search params to Zustand store (one-way: URL → Store)
  useEffect(() => {
    setFilters({
      selectedScopeStatus: scopeStatus,
      selectedRoleFilter: roleFilter,
      excludedTeams: new Set(excludedTeams),
      excludedProjects: new Set(excludedProjects),
    });
  }, [scopeStatus, roleFilter, excludedTeams, excludedProjects, setFilters]);

  // Convert arrays back to Sets for filtering logic
  const excludedTeamsSet = useMemo(() => new Set(excludedTeams), [excludedTeams]);
  const excludedProjectsSet = useMemo(() => new Set(excludedProjects), [excludedProjects]);

  // Handle member selection from query params
  useEffect(() => {
    if (memberId) {
      const member = teamMemberIndex.get(memberId);
      // Only show modal if member exists and is not archived
      const isArchived = member?.status.some(
        (s) => s.toLowerCase() === "archived" || s.toLowerCase().includes("archive"),
      );
      if (member && !isArchived) {
        setSelectedMember(member);
        setIsModalOpen(true);
      }
    } else {
      setIsModalOpen(false);
    }
  }, [memberId, teamMemberIndex]);

  const handleModalChange = (open: boolean) => {
    setIsModalOpen(open);
    if (!open) {
      // Clear the memberId param when closing modal, keep other filters
      const { memberId: _, ...rest } = searchParams;
      void navigate({ search: omitDefaultSearchParams(rest) });
    }
  };

  // Collect all person IDs from visible members for batch avatar loading
  const allPersonIds = useMemo(() => {
    const ids = new Set<string>();
    const roleFilteredMembers = teamMembers.filter((member) =>
      matchesRoleFilter(member.role, roleFilter),
    );

    for (const member of roleFilteredMembers) {
      if (!excludedTeamsSet.has(member.team)) {
        member.notionUserIds?.forEach((id) => ids.add(id));
      }
    }
    return Array.from(ids);
  }, [excludedTeamsSet, teamMembers, roleFilter]);

  // Prefetch all avatars in a single batch
  useAvatars(allPersonIds);

  const teams = useMemo(() => {
    const teamMap: Record<string, TeamMember[]> = {};

    // Filter members by role first
    let roleFilteredMembers = teamMembers.filter((member) =>
      matchesRoleFilter(member.role, roleFilter),
    );

    // If some projects are selected (not all excluded), only show members who are in scope
    // When excludedProjectsSet.size === allProjects.length, no projects are selected (all excluded)
    // When excludedProjectsSet.size === 0, all projects are selected (none excluded)
    // When 0 < excludedProjectsSet.size < allProjects.length, some projects are selected
    const hasProjectSelection =
      excludedProjectsSet.size > 0 && excludedProjectsSet.size < allProjects.length;
    if (hasProjectSelection) {
      roleFilteredMembers = roleFilteredMembers.filter((member) => {
        // Check if member is in scope
        const memberEntities = inScopeMemberEntitiesMap.get(member.id);
        if (!memberEntities) return false;

        // Check if member has at least one project that is not excluded
        const hasProjects = memberEntities.some(
          ([type, entityId]) => type === "projects" && !excludedProjectsSet.has(entityId),
        );
        return hasProjects;
      });
    } else if (excludedProjectsSet.size === allProjects.length) {
      // All projects are excluded (none selected), show no members
      roleFilteredMembers = [];
    }

    for (const member of roleFilteredMembers) {
      teamMap[member.team] = [...(teamMap[member.team] ?? []), member];
    }

    // Use allTeams order (which respects custom ordering) instead of alphabetical
    return allTeams
      .map((teamName) => [teamName, teamMap[teamName] ?? []] as [string, TeamMember[]])
      .filter(([teamName, members]) => !excludedTeamsSet.has(teamName) && members.length > 0);
  }, [
    excludedTeamsSet,
    teamMembers,
    allTeams,
    allProjects,
    roleFilter,
    excludedProjectsSet,
    inScopeMemberEntitiesMap,
  ]);

  const shouldShowLoading = isLoading && teams.length === 0;

  return (
    <>
      <div className="h-full overflow-auto">
        <LoadingCard
          isLoading={shouldShowLoading}
          className="flex items-center justify-center h-full w-full"
        >
          <div className="flex flex-col gap-8 relative h-full w-full">
            {teams.map(([teamName, members], index) => {
              return (
                <TeamSection
                  key={index}
                  teamName={teamName}
                  members={members}
                  workstreamsByMember={workstreamsByMember}
                  workstreamPreviewWindow={workstreamPreviewWindow}
                />
              );
            })}
          </div>
        </LoadingCard>
      </div>

      <MemberModal member={selectedMember} open={isModalOpen} onOpenChange={handleModalChange} />
    </>
  );
}

export const Route = createFileRoute("/")({
  component: Dashboard,
  validateSearch: zSearch,
});
